import { useState, useEffect, useCallback } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDropzone } from 'react-dropzone';
import {
  X,
  Calendar,
  CheckCircle2,
  Clock3,
  Clipboard,
  MessageSquare,
  Maximize,
  Minimize,
  UploadCloud,
  FileText,
  File,
  Trash2,
  Save // Added Save icon for the submit button
} from "lucide-react";
import { getRequest, postRequest, putRequest } from "../../services/apiService";

export default function UpdateMileStoneTask({ shipId, equipmentId, onClose }) {
  const [milestones, setMilestones] = useState([]); // This will act as our "headers"
  const [selectedMilestone, setSelectedMilestone] = useState(null); // This will act as our "activeHeader"
  const [taskTransaction, setTaskTransaction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [formData, setFormData] = useState({
    status: "In Progress", // Default to "In Progress" as per UpdateTaskSubtaskForm
    percentage: 0,
    actual_start: "", // Corresponds to startDate in UpdateTaskSubtaskForm
    actual_finish: "", // Corresponds to endDate in UpdateTaskSubtaskForm
    remarks: ""
  });

  // Function to get dynamic colors for headers (milestones)
  const getHeaderColor = (index) => {
    const colorsArray = [
      { bg: "bg-blue-600", text: "text-white", shadow: "shadow-blue-200" },
      { bg: "bg-green-600", text: "text-white", shadow: "shadow-green-200" },
      { bg: "bg-purple-600", text: "text-white", shadow: "shadow-purple-200" },
      { bg: "bg-red-600", text: "text-white", shadow: "shadow-red-200" },
      { bg: "bg-yellow-600", text: "text-white", shadow: "shadow-yellow-200" },
    ];
    return colorsArray[index % colorsArray.length];
  };

  // Fetch milestones (acting as headers)
  useEffect(() => {
    const fetchMilestones = async () => {
      try {
        const response = await getRequest(`pert/task-transaction-api/?task_type=milestone&project_id=${shipId}`);
        setMilestones(response.data);
      } catch (error) {
        console.error("Error fetching milestones:", error);
        toast.error("Failed to fetch milestones.");
      }
    };
    fetchMilestones();
  }, [shipId]);

  // Fetch task transaction details for the selected milestone
  useEffect(() => {
    const fetchTaskTransaction = async () => {
      if (!selectedMilestone || !shipId) {
        setTaskTransaction(null);
        setFormData({
            status: "In Progress", // Reset to "In Progress"
            percentage: 0,
            actual_start: "",
            actual_finish: "",
            remarks: ""
        });
        setUploadedFiles([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await getRequest(
          `transaction/task-transaction?project_id=${shipId}&milestone=${selectedMilestone.id}`
        );

        if (response.data.length > 0) {
          const transaction = response.data[0];
          setTaskTransaction(transaction);
          setFormData({
            status: transaction.is_completed ? "Completed" : "In Progress",
            percentage: transaction.actual_completion_percent || 0,
            actual_start: transaction.actual_start?.split('T')[0] || "",
            actual_finish: transaction.actual_finish?.split('T')[0] || "",
            remarks: transaction.description || ""
          });
          setUploadedFiles([]); // Reset files, then load if transaction has any linked files
          // In a real app, you'd fetch linked files here:
          // if (transaction.files && transaction.files.length > 0) {
          //   setUploadedFiles(transaction.files.map(file => ({ name: file.name, preview: file.url })));
          // }
        } else {
          setTaskTransaction(null);
          setFormData({
            status: "In Progress", // Reset to "In Progress"
            percentage: 0,
            actual_start: "",
            actual_finish: "",
            remarks: ""
          });
          setUploadedFiles([]);
        }
      } catch (error) {
        console.error("Error fetching task transaction:", error);
        toast.error("Failed to fetch milestone transaction data.");
        setTaskTransaction(null);
        setUploadedFiles([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTaskTransaction();
  }, [selectedMilestone, shipId]);

  // React-dropzone setup for file uploads
  const onDrop = useCallback(acceptedFiles => {
    setUploadedFiles(prev => [...prev, ...acceptedFiles.map(file => Object.assign(file, {
      preview: URL.createObjectURL(file)
    }))]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: true });

  const removeFile = (fileToRemove) => {
    setUploadedFiles(prev => prev.filter(file => file !== fileToRemove));
    URL.revokeObjectURL(fileToRemove.preview); // Clean up memory
  };

  // Handle form submission (create or update transaction)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const payload = {
      task: null,
      resource_type: "milestone",
      project: shipId ? parseInt(shipId) : null,
      milestone: selectedMilestone ? parseInt(selectedMilestone.id) : null,
      equipment: null,
      system: null,
      compartment: null,
      mpr_unit: null,
      mpr_block: null,
      description: formData.remarks || null,
      actual_start: formData.actual_start || null,
      actual_finish: formData.actual_finish || null,
      actual_completion_percent: parseFloat(formData.percentage) || 0,
      is_completed: formData.status === 'Completed'
    };

    const formDataWithFiles = new FormData();
    for (const key in payload) {
      if (payload[key] !== null) {
        formDataWithFiles.append(key, payload[key]);
      }
    }
    uploadedFiles.forEach((file) => {
      formDataWithFiles.append(`files`, file);
    });

    try {
      if (taskTransaction) {
        await putRequest(
          `transaction/task-transaction/${taskTransaction.id}/`,
          formDataWithFiles
        );
      } else {
        await postRequest(
          "transaction/task-transaction/",
          formDataWithFiles
        );
      }
      toast.success("Milestone saved successfully!", {
        position: "top-right", autoClose: 3000, hideProgressBar: false,
        closeOnClick: true, pauseOnHover: true, draggable: true, theme: "colored",
      });
      setUploadedFiles([]);
      if (selectedMilestone) {
          setSelectedMilestone({ ...selectedMilestone }); // Trigger re-fetch
      }
    } catch (error) {
      console.error("Operation failed:", error.response?.data || error.message);
      toast.error("Failed to save milestone. Please try again.", {
        position: "top-right", autoClose: 3000, hideProgressBar: false,
        closeOnClick: true, pauseOnHover: true, draggable: true, theme: "colored",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    onClose();
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  if (!isModalOpen) return null;

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        style={{ zIndex: 9999 }}
      />

      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className={`bg-white rounded-lg shadow-xl flex flex-col overflow-hidden animate-fadeIn ${
          isFullScreen ? 'fixed inset-0 m-0 rounded-none' : 'w-full max-w-7xl h-5/6'
        }`}>
          <div className="bg-[#1e3a8a] text-white px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-bold">
              {taskTransaction ? "Update Milestone Transaction" : "Create Milestone Transaction"}
            </h2>
            <div className="flex items-center">
              <button
                onClick={toggleFullScreen}
                className="p-1 rounded-full hover:bg-gray-700 transition-colors mr-2"
                disabled={isLoading}
                title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                {isFullScreen ?
                  <Minimize className="w-6 h-6" /> :
                  <Maximize className="w-6 h-6" />
                }
              </button>
              <button
                onClick={handleCloseModal}
                className="p-1 rounded-full hover:bg-gray-700 transition-colors"
                disabled={isLoading}
                title="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="flex flex-1 flex-col overflow-hidden">
            {/* Horizontal Milestone Headers */}
            <div className="p-4 bg-white border-b border-gray-200 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400">
              {milestones.length > 0 ? (
                <div className="flex space-x-3 pb-2">
                  {milestones.map((milestone, index) => {
                    const colors = getHeaderColor(index);
                    return (
                      <button
                        key={milestone.id}
                        className={`px-6 py-3 whitespace-nowrap rounded-lg transition-all duration-300 transform border-2 shadow-lg ${
                          selectedMilestone?.id === milestone.id
                            ? `${colors.bg} ${colors.text} font-semibold scale-105 shadow-xl ${colors.shadow}`
                            : `bg-white text-gray-700 border-gray-200 hover:shadow-lg hover:scale-102 hover:border-gray-300`
                        }`}
                        onClick={() => setSelectedMilestone(milestone)}
                        disabled={isLoading}
                      >
                        <span className="flex items-center">
                          {milestone.name}
                          {selectedMilestone?.id === milestone.id && (
                            <div className="ml-2 w-2 h-2 bg-white rounded-full animate-pulse"></div>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No milestones available.</p>
              )}
            </div>

            {/* Main Content Area: Form for selected milestone */}
            <div className="flex-1 p-6 overflow-y-auto bg-white scrollbar-thin">
              {isLoading && selectedMilestone ? (
                <div className="h-full flex flex-col items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  <p className="mt-4 text-gray-600">Loading transaction data...</p>
                </div>
              ) : selectedMilestone ? (
                <form onSubmit={handleSubmit} className="animate-fadeIn">
                  <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                    <h3 className="text-2xl font-bold mb-6 text-gray-800 pb-4 border-b border-gray-200 flex items-center">
                      <CheckCircle2 className="w-6 h-6 mr-3 text-green-600" />
                      {selectedMilestone?.name}
                    </h3>

                    <div className="space-y-6">
                      {/* Status */}
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-green-200">
                        <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                          <CheckCircle2 className="w-5 h-5 mr-2 text-green-600" />
                          Status
                        </label>
                        <select
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm bg-white"
                          value={formData.status}
                          onChange={(e) => setFormData({...formData, status: e.target.value})}
                          disabled={isLoading}
                        >
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                          <option value="Not Started">Not Started</option> {/* Added Not Started back */}
                        </select>
                      </div>

                      {/* Completion Percentage */}
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
                        <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                          <Clock3 className="w-5 h-5 mr-2 text-blue-600" />
                          Completion Percentage:
                          <span className="ml-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-bold">
                            {formData.percentage.toFixed(0)}% {/* Changed toFixed(0) */}
                          </span>
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={formData.percentage}
                          onChange={(e) => setFormData({...formData, percentage: parseFloat(e.target.value)})}
                          className="w-full h-3 bg-gradient-to-r from-blue-200 to-purple-200 rounded-lg appearance-none cursor-pointer slider"
                          disabled={isLoading}
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-2 font-medium">
                          <span>0%</span>
                          <span>25%</span>
                          <span>50%</span>
                          <span>75%</span>
                          <span>100%</span>
                        </div>
                      </div>

                      {/* Actual Start/Finish Dates */}
                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                          <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                            <Calendar className="w-5 h-5 mr-2 text-orange-600" />
                            Actual Start Date
                          </label>
                          <input
                            type="date"
                            value={formData.actual_start} 
                            onChange={(e) => setFormData({...formData, actual_start: e.target.value})}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent shadow-sm bg-white"
                            disabled={isLoading}
                          />
                        </div>
                        <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                          <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                            <Calendar className="w-5 h-5 mr-2 text-red-600" />
                            Actual End Date
                          </label>
                          <input
                            type="date"
                            value={formData.actual_finish} 
                            onChange={(e) => setFormData({...formData, actual_finish: e.target.value})}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm bg-white"
                            disabled={isLoading}
                          />
                        </div>
                      </div>

                      {/* Remarks */}
                      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                        <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                          <MessageSquare className="w-5 h-5 mr-2 text-purple-600" />
                          Remarks
                        </label>
                        <textarea
                          rows="4"
                          value={formData.remarks}
                          onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                          className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none shadow-sm bg-white"
                          placeholder="Add any additional notes or comments here..."
                          disabled={isLoading}
                        />
                      </div>

                      {/* File Upload Section */}
                      <div className="bg-gradient-to-r from-green-50 to-teal-50 p-4 rounded-lg border border-green-200">
                        <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                          <UploadCloud className="w-5 h-5 mr-2 text-green-600" />
                          Upload Files
                        </label>
                        <div {...getRootProps()} className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all duration-200
                          ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white hover:border-blue-400'}`}
                          disabled={isLoading}
                        >
                          <input {...getInputProps()} />
                          {
                            isDragActive ?
                              <p className="text-blue-600">Drop the files here ...</p> :
                              <p className="text-gray-500">Drag 'n' drop some files here, or click to select files (images, PDFs, documents)</p>
                          }
                        </div>

                        {uploadedFiles.length > 0 && (
                          <div className="mt-4 border-t border-gray-200 pt-4">
                            <h4 className="text-md font-semibold text-gray-700 mb-3">Preview Uploaded Files:</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {uploadedFiles.map((file, index) => (
                                <div key={index} className="relative bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center space-x-3 shadow-sm">
                                  <a
                                    href={file.preview}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-3 group"
                                  >
                                    {file.type.startsWith('image/') ? (
                                      <img
                                        src={file.preview}
                                        alt={file.name}
                                        className="h-16 w-16 object-cover rounded-md border border-gray-300 group-hover:scale-105 transition-transform duration-200"
                                        onLoad={() => URL.revokeObjectURL(file.preview)}
                                      />
                                    ) : file.type === 'application/pdf' ? (
                                      <div className="h-16 w-16 flex items-center justify-center bg-red-100 text-red-600 rounded-md group-hover:scale-105 transition-transform duration-200">
                                        <FileText className="w-8 h-8" />
                                      </div>
                                    ) : (
                                      <div className="h-16 w-16 flex items-center justify-center bg-blue-100 text-blue-600 rounded-md group-hover:scale-105 transition-transform duration-200">
                                        <File className="w-8 h-8" />
                                      </div>
                                    )}
                                    <div className="flex-1 overflow-hidden">
                                      <p className="text-sm font-medium text-gray-800 truncate group-hover:underline" title={file.name}>{file.name}</p>
                                      <p className="text-xs text-gray-500">{ (file.size / 1024).toFixed(2) } KB</p>
                                    </div>
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => removeFile(file)}
                                    className="p-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors duration-200"
                                    title="Remove file"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Submit Button */}
                      <div className="flex justify-end pt-4">
                        <button
                          type="submit"
                          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-8 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-opacity-50 flex items-center shadow-lg font-semibold"
                          disabled={isLoading}
                        >
                          <Save className="w-5 h-5 mr-2" />
                          {isLoading ? "Saving..." : (taskTransaction ? "Update Milestone" : "Create Milestone")}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-500 animate-pulse">
                  <Clipboard className="w-16 h-16 mb-4 opacity-30" />
                  <p className="text-lg">Select a milestone from the top to begin</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Global styles
const style = document.createElement('style');
style.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideDown {
    from { max-height: 0; opacity: 0; }
    to { max-height: 300px; opacity: 1; }
  }

  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out forwards;
  }

  .animate-slideDown {
    animation: slideDown 0.3s ease-out forwards;
  }

  .scrollbar-thin::-webkit-scrollbar {
    height: 8px; /* Adjusted for horizontal scrollbar */
    width: 8px; /* Adjusted for vertical scrollbar, though mainly horizontal now */
  }

  .scrollbar-thin::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px; /* Rounded track */
  }

  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 8px; /* Rounded thumb */
  }

  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  /* Custom slider styles for consistent look */
  input[type="range"].slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #4A90E2; /* Blue color */
    cursor: pointer;
    border: 3px solid #fff; /* White border */
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    transition: background .15s ease-in-out;
  }

  input[type="range"].slider::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #4A90E2; /* Blue color */
    cursor: pointer;
    border: 3px solid #fff;
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    transition: background .15s ease-in-out;
  }

  input[type="range"].slider::-ms-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #4A90E2; /* Blue color */
    cursor: pointer;
    border: 3px solid #fff;
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    transition: background .15s ease-in-out;
  }

  input[type="range"].slider:hover::-webkit-slider-thumb {
    background: #357ABD; /* Darker blue on hover */
  }
  input[type="range"].slider:hover::-moz-range-thumb {
    background: #357ABD;
  }
  input[type="range"].slider:hover::-ms-thumb {
    background: #357ABD;
  }

  /* Track styles */
  input[type="range"].slider::-webkit-slider-runnable-track {
    width: 100%;
    height: 8px;
    background: #e0e0e0;
    border-radius: 5px;
  }

  input[type="range"].slider::-moz-range-track {
    width: 100%;
    height: 8px;
    background: #e0e0e0;
    border-radius: 5px;
  }

  input[type="range"].slider::-ms-track {
    width: 100%;
    height: 8px;
    background: #e0e0e0;
    border-radius: 5px;
  }
`;
document.head.appendChild(style);